import React from "react";
import EmptyState from "@/components/ui/empty-state";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NavLink, useParams } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  TriangleAlert,
  CircleAlert,
  Filter,
  CheckIcon,
  XIcon,
  CheckCircleIcon,
  XCircleIcon,
  FilterXIcon,
} from "lucide-react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Pin } from "@/components/ui/pin";
import { useCreatorsStore, useInboxStore } from "@/stores";
import { STATUS_MAP } from "@/constants/statuses";
import { Creator } from "@/types/types";
import { Badge } from "@/components/ui/badge";
import CreatorInbox from "../inbox/CreatorInbox";
import AutopilotToggle from "../inbox/AutopilotToggle";

/**
 * Amy-v2 review state where the latest submitted file could NOT be watched
 * (WeTransfer wall, over the size cap, …) — the verdict is fully on the
 * team, so the row gets the same warning treatment as a handoff.
 */
const needsUnwatchedReview = (creator: Creator): boolean => {
  if (!creator.status?.endsWith("-review")) return false;
  const artifacts = creator.eve_phase?.artifacts;
  const latest = artifacts?.length ? artifacts[artifacts.length - 1] : undefined;
  return (latest?.media ?? []).some((m) => !m.watched);
};

export default function InboxTab() {
  const { creator_id } = useParams();
  const { creators } = useCreatorsStore();
  const { setActiveCreatorName } = useInboxStore();

  const [search, setSearch] = React.useState("");

  // Initialize selectedStatuses with all statuses
  const [selectedStatuses, setSelectedStatuses] = React.useState<Set<string>>(
    new Set(Object.keys(STATUS_MAP))
  );

  const toggleStatus = (status: string) => {
    setSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedStatuses(new Set(Object.keys(STATUS_MAP)));
  };

  const deselectAll = () => {
    setSelectedStatuses(new Set());
  };

  const getCreatorInboxPath = (creatorId: string) => {
    const pathSegments = location.pathname.split("/");
    const inboxIndex = pathSegments.findIndex((segment) => segment === "inbox");

    if (inboxIndex !== -1) {
      if (inboxIndex + 1 < pathSegments.length) {
        pathSegments[inboxIndex + 1] = creatorId;
      } else {
        pathSegments.push(creatorId);
      }
    } else {
      pathSegments.push("inbox", creatorId);
    }

    return pathSegments.join("/");
  };

  React.useEffect(() => {
    if (creator_id) {
      setActiveCreatorName(
        creators.find((creator) => creator._id === creator_id)?.username || ""
      );
    }
  }, [creator_id]);

  return (
    <ResizablePanelGroup direction="horizontal" className="w-full h-full flex">
      <ResizablePanel defaultSize={25} minSize={15} maxSize={35}>
        <ScrollArea className="w-full flex flex-col h-full bg-background">
          <div className="flex items-center gap-2 bg-chrome h-12.25 px-2 sticky top-0 z-20 border-b">
            <Input
              className="flex-1 rounded-none bg-transparent border-none peer h-full shadow-none focus-visible:ring-0"
              placeholder="Search creators . . ."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="flex items-center gap-2">
              {search.length > 0 && (
                <Button
                  variant="destructive-ghost"
                  className="size-6 rounded-sm"
                  onClick={() => setSearch("")}
                  scope={false}
                >
                  <XIcon />
                </Button>
              )}

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="muted-ghost"
                    size="icon"
                    className="size-8"
                  >
                    <Filter className="size-4" />
                    {selectedStatuses.size < Object.keys(STATUS_MAP).length && (
                      <Pin variant="default" className="absolute -top-1 -right-1" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="max-w-64 w-auto p-1 rounded-lg"
                  align="start"
                >
                  <div className="flex flex-row items-center gap-2 mb-2">
                    <h4 className="font-medium text-xs text-muted-foreground p-2">
                      Filter by status
                    </h4>
                    <Button
                      variant="muted-ghost"
                      onClick={selectAll}
                      className="text-xs size-6.5 ml-auto rounded-sm"
                      scope={false}
                      title="Select all statuses"
                    >
                      <CheckCircleIcon />
                    </Button>
                    <Button
                      variant="muted-ghost"
                      onClick={deselectAll}
                      className="text-xs size-6.5 rounded-sm"
                      scope={false}
                      title="Deselect all statuses"
                    >
                      <XCircleIcon />
                    </Button>
                  </div>
                  <div className="flex flex-col">
                    {Object.entries(STATUS_MAP)
                      .filter(([key]) =>
                        creators.some((creator) => creator.status === key)
                      )
                      .map(([key, status]) => (
                        <div
                          key={key}
                          className={`flex items-center space-x-2 cursor-pointer hover:bg-muted p-1 px-2 rounded-sm`}
                          onClick={() => toggleStatus(key)}
                        >
                          <span className="w-4">
                            {selectedStatuses.has(key) && (
                              <CheckIcon className="size-4" />
                            )}
                          </span>
                          <Badge className={`${status.color} rounded-sm gap-0`}>
                            {status.icon}
                            {status.name}
                          </Badge>
                          <span className="ml-auto text-xs text-muted-foreground pl-2">
                            {
                              creators.filter(
                                (creator) => creator.status === key
                              ).length
                            }
                          </span>
                        </div>
                      ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="">
            {(() => {
              const filteredCreators = creators
                .filter(
                  (creator) =>
                    creator.thread_id &&
                    typeof creator.thread_id === "string" &&
                    creator.thread_id.trim() !== "" &&
                    selectedStatuses.has(creator.status || "pending")
                )
                .filter((creator) =>
                  creator.username.toLowerCase().includes(search.toLowerCase())
                );

              // Group creators by status
              const groupedCreators = filteredCreators.reduce(
                (acc, creator) => {
                  const status = creator.status || "pending";
                  if (!acc[status]) acc[status] = [];
                  acc[status].push(creator);
                  return acc;
                },
                {} as Record<string, typeof creators>
              );

              // Get status keys in the order they appear in STATUS_MAP
              const statusOrder = Object.keys(STATUS_MAP);
              const orderedGroups = statusOrder.filter(
                (status) => groupedCreators[status]
              );

              if (orderedGroups.length === 0) {
                return (
                  <div className="flex items-center justify-center h-full p-4">
                    <EmptyState icon={<FilterXIcon />} text="No creators match the selected filters" />
                  </div>
                );
              }

              return orderedGroups.map((status) => (
                <div key={status} className="">
                  {/* Status Label */}
                  <div className="sticky top-12.25 z-10 bg-background">
                    <Badge
                      className={`${STATUS_MAP[status as keyof typeof STATUS_MAP].color
                        } rounded-none gap-0 w-full justify-start [&_svg]:mr-2`}
                    >
                      {STATUS_MAP[status as keyof typeof STATUS_MAP].icon}
                      {STATUS_MAP[status as keyof typeof STATUS_MAP].name}
                      <span className="ml-auto">
                        {groupedCreators[status].length}
                      </span>
                    </Badge>
                  </div>

                  {/* Creators in this status */}
                  {groupedCreators[status].map((creator) => (
                    <NavLink
                      key={creator._id}
                      className={`flex items-center gap-2 p-2 pr-5 cursor-pointer duration-75 transition-all pl-4
                      ${creator_id === creator._id
                          ? "text-primary border-l-4 border-l-primary"
                          : "hover:bg-muted"
                        }
                      ${creator.hand_off_to_user === true || needsUnwatchedReview(creator)
                          ? "dark:text-warning text-warning bg-warning/5 border-l-warning hover:bg-warning/10 relative"
                          : ""
                        }
                      ${creator?.error && creator.error.length > 0
                          ? "dark:text-destructive text-destructive bg-destructive/5 border-l-destructive hover:bg-destructive/10 relative"
                          : ""
                        }
                    `}
                      to={getCreatorInboxPath(creator._id)}
                    >
                      {(creator.hand_off_to_user === true || needsUnwatchedReview(creator)) && (
                        <div className="absolute inset-0 bg-[repeating-linear-gradient(315deg,--theme(--color-warning/0.3)_0,--theme(--color-warning/0.5)_1px,transparent_0,transparent_50%)] bg-size-[10px_10px] bg-fixed opacity-30"></div>
                      )}

                      {creator?.error && creator.error.length > 0 && (
                        <div className="absolute inset-0 bg-[repeating-linear-gradient(315deg,--theme(--color-destructive/0.3)_0,--theme(--color-destructive/0.5)_1px,transparent_0,transparent_50%)] bg-size-[10px_10px] bg-fixed opacity-30"></div>
                      )}

                      <Avatar
                        className={`size-10 border ${creator.hand_off_to_user || needsUnwatchedReview(creator)
                            ? "border-warning dark:border-warning"
                            : creator?.error && creator.error.length > 0
                              ? "border-destructive dark:border-destructive"
                              : ""
                          }`}
                      >
                        <AvatarImage src={creator.avatar} />
                        <AvatarFallback>
                          {creator.username.substring(0, 2)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="truncate w-full flex flex-col items-start">
                        <span className="text-sm font-medium">
                          {creator.username}
                        </span>
                        <span className="text-xs opacity-60">
                          {creator.email}
                        </span>
                      </div>

                      {creator.hand_off_to_user === true && (
                        <TriangleAlert className="size-5 text-warning dark:warning" />
                      )}

                      {creator.hand_off_to_user !== true && needsUnwatchedReview(creator) && (
                        <Tooltip>
                          <TooltipTrigger>
                            <TriangleAlert className="size-5 text-warning dark:warning" />
                          </TooltipTrigger>
                          <TooltipContent>
                            Amy could not watch the submitted file — review it yourself
                          </TooltipContent>
                        </Tooltip>
                      )}

                      {creator.error && creator.error.length > 0 && (
                        <CircleAlert className="size-5 text-destructive dark:destructive" />
                      )}

                      <Tooltip>
                        <TooltipTrigger>
                          <Pin
                            variant={
                              creator.autopilot ? "success" : "destructive"
                            }
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          {creator.autopilot
                            ? "Running on autopilot"
                            : "Overridden thread (Amy will not reply)"}
                        </TooltipContent>
                      </Tooltip>
                    </NavLink>
                  ))}
                </div>
              ));
            })()}
          </div>
        </ScrollArea>
      </ResizablePanel>

      <ResizableHandle withHandle />

      <ResizablePanel
        defaultSize={75}
        className="flex-1 border border-l-0 border-t-0 border-b-0 bg-background"
      >
        <div className="h-full flex flex-col">
          {creator_id && (
            <div className="flex items-center justify-end gap-1.5 px-2 min-h-12 bg-chrome border-b">
              <AutopilotToggle creator_id={creator_id} />
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <CreatorInbox creator_id={creator_id} />
          </div>
        </div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
